import { v4 as uuid } from 'uuid'

import { UseCase } from '@/domain/core/UseCase'
import { Result } from '@/domain/protocols/Result'
import {
  ALLOWED_USER_GEOMETRY_EXTENSIONS,
  ConvertGeometryControl,
  MAX_USER_GEOMETRIES,
  PreparedUserGeometryData,
  USER_GEOMETRY_MAX_FILE_BYTES,
  UserGeometryExtension
} from '@/domain/protocols/UserGeometry'

import type { IUserGeometryRepository } from '@/data/UserGeometryRepository/IUserGeometryRepository'
import type { ConvertGeometryResult } from '@/data/UserGeometryRepository/userGeometryFileConversion'
import {
  convertKMLToFlatGeobuf,
  convertKMZToFlatGeobuf
} from '@/data/UserGeometryRepository/userGeometryFileConversion'

export interface PrepareUserGeometryRequest {
  file: File
  control?: ConvertGeometryControl
}

// The KML/KMZ converters, injectable so the use case can be unit-tested in isolation.
export interface UserGeometryFileConverters {
  convertKml: (
    source: ArrayBuffer | ArrayBufferView | string,
    options?: ConvertGeometryControl & { geometryId?: string }
  ) => Promise<ConvertGeometryResult>
  convertKmz: (
    source: ArrayBuffer | ArrayBufferView,
    options?: ConvertGeometryControl & { geometryId?: string }
  ) => Promise<ConvertGeometryResult>
}

const DEFAULT_CONVERTERS: UserGeometryFileConverters = {
  convertKml: convertKMLToFlatGeobuf,
  convertKmz: convertKMZToFlatGeobuf
}

// Validates the picked file, enforces the per-account geometry cap, converts the
// KML/KMZ to FlatGeobuf bytes (reporting progress / honouring cancellation) and
// returns the preview payload. Nothing is persisted here — the web pipeline keeps
// the bytes in memory, so there are no temp files to clean up on failure/cancel.
export class PrepareUserGeometryUseCase implements UseCase<
  PrepareUserGeometryRequest,
  Promise<Result<PreparedUserGeometryData>>
> {
  constructor(
    private readonly repository: IUserGeometryRepository,
    private readonly converters: UserGeometryFileConverters = DEFAULT_CONVERTERS
  ) {}

  public static getExtension(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.')
    if (dotIndex < 0) return ''
    return fileName.slice(dotIndex + 1).toLowerCase()
  }

  public static getBaseName(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.')
    if (dotIndex < 0) return fileName
    return fileName.slice(0, dotIndex)
  }

  public static validate(file: File): Result<void> {
    const ext = PrepareUserGeometryUseCase.getExtension(file.name) as UserGeometryExtension

    if (!ALLOWED_USER_GEOMETRY_EXTENSIONS.includes(ext)) {
      return Result.fail('INVALID_FORMAT')
    }

    if (file.size > USER_GEOMETRY_MAX_FILE_BYTES) {
      return Result.fail('KMZ_FILE_TOO_LARGE')
    }

    return Result.ok()
  }

  async execute(request: PrepareUserGeometryRequest): Promise<Result<PreparedUserGeometryData>> {
    const { file, control } = request

    const validation = PrepareUserGeometryUseCase.validate(file)
    if (validation.isFailure) {
      return Result.fail(validation.error)
    }

    try {
      // Block when the per-account cap is reached; a failed read is tolerated so a
      // transient storage error doesn't prevent importing.
      const geometriesResult = await this.repository.getGeometries()
      if (geometriesResult.isSuccess && geometriesResult.getValue().length >= MAX_USER_GEOMETRIES) {
        return Result.fail('USER_GEOMETRIES_LIMIT_REACHED')
      }

      const extension = PrepareUserGeometryUseCase.getExtension(file.name)
      const originalBytes = new Uint8Array(await file.arrayBuffer())
      const geometryId = uuid()

      const conversion =
        extension === 'kmz'
          ? await this.converters.convertKmz(originalBytes, { ...control, geometryId })
          : await this.converters.convertKml(originalBytes, { ...control, geometryId })

      if (!conversion.success) {
        // CANCELLED is surfaced too; the presentation layer keeps it silent.
        return Result.fail(conversion.diagnostics.code)
      }

      const prepared: PreparedUserGeometryData = {
        ...conversion.data,
        baseName: PrepareUserGeometryUseCase.getBaseName(file.name),
        originalFileName: file.name,
        originalBytes
      }

      return Result.ok(prepared)
    } catch (error) {
      return Result.fail(error)
    }
  }
}
